import { Form, Input, Select, Space } from 'antd';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import styled from 'styled-components';
import countries from 'utils/countries';
import { ErrorDiv, ErrorLabel } from '../formInput/styles';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import {
    FieldWrapper,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    MetaSection,
} from 'components/ui/composed/declarations/formInput/DeclarationInput.styles';

type Props = {
    phoneNumberFieldProps: FieldInputProps<any>;
    phoneNumberFieldMeta: FieldMetaProps<any>;
    phoneNumberFieldHelpers: FieldHelperProps<any>;
    codeFieldProps: FieldInputProps<any>;
    codeFieldMeta: FieldMetaProps<any>;
    codeFieldHelpers: FieldHelperProps<any>;
    label?: string;
    required?: boolean;
    // Renders the field using the same responsive label-left/input-right layout as
    // DeclarationInput fields, instead of always stacking the label above the input.
    alignLabel?: boolean;
};

const borderRadius = 6;

const phoneCodeOptions = countries.map((country) => {
    const id = country.phone;
    const value = country.phone;
    const label = `${country.phone} - ${country.code} - ${country.label}`;
    return { id, value, label, key: country.code };
});

const StyledSelect = styled(Select)`
    width: max-content;
    white-space: nowrap;

    && .ant-select-selector {
        border-top-left-radius: ${borderRadius}px !important;
        border-bottom-left-radius: ${borderRadius}px !important;
    }
`;

const FormPhoneNumber = ({
    codeFieldProps,
    codeFieldHelpers,
    codeFieldMeta,
    phoneNumberFieldProps,
    phoneNumberFieldHelpers,
    phoneNumberFieldMeta,
    label = 'Phone Number',
    required = false,
    alignLabel = false,
}: Props) => {
    const combinedTouched = Boolean(phoneNumberFieldMeta?.touched || codeFieldMeta?.touched);
    const combinedHasError = Boolean(phoneNumberFieldMeta?.error || codeFieldMeta?.error);

    const combinedMessage =
        (phoneNumberFieldMeta?.error as string | undefined) ?? (codeFieldMeta?.error as string | undefined) ?? '';

    const fieldGroup = (
        <>
            <Input.Group compact style={{ display: 'flex' }}>
                <Form.Item noStyle>
                    <StyledSelect
                        allowClear
                        showSearch
                        options={phoneCodeOptions}
                        optionLabelProp="value" // shows "+359"
                        placeholder="Code"
                        value={codeFieldProps.value}
                        onBlur={() => codeFieldHelpers?.setTouched?.(true)}
                        onChange={(val) => codeFieldHelpers?.setValue(val)}
                        dropdownMatchSelectWidth={false}
                        status={combinedHasError && combinedTouched ? 'error' : undefined}
                        filterOption={(input, option) => {
                            const q = input.toLowerCase();
                            const phone = option?.value?.toLowerCase?.() ?? '';
                            const label = option?.label?.toLowerCase?.() ?? '';
                            const country = countries.find((c) => c.phone === option?.value);
                            const code = country?.code?.toLowerCase?.() ?? '';
                            return phone.includes(q) || label.includes(q) || code.includes(q);
                        }}
                    />
                </Form.Item>

                <Form.Item noStyle style={{ flex: 1 }}>
                    <Input
                        allowClear
                        placeholder="Enter phone number"
                        name={phoneNumberFieldProps.name}
                        style={{
                            borderTopRightRadius: borderRadius,
                            borderBottomRightRadius: borderRadius,
                        }}
                        value={phoneNumberFieldProps.value}
                        onChange={(e) => phoneNumberFieldHelpers?.setValue(e.target.value)}
                        onBlur={() => phoneNumberFieldHelpers?.setTouched?.(true)}
                        status={combinedHasError && combinedTouched ? 'error' : undefined}
                    />
                </Form.Item>
            </Input.Group>

            <ErrorDiv error={combinedHasError} touched={combinedTouched} condensed>
                {combinedHasError && combinedTouched && <ErrorLabel>{combinedMessage}</ErrorLabel>}
            </ErrorDiv>
        </>
    );

    if (alignLabel) {
        return (
            <FieldWrapper $viewOnly={false} $template={false}>
                <MetaSection>
                    <LabelDiv condensed $labelSpacing="medium">
                        <InputLabel>
                            {required && <InputLabelRequired right>*</InputLabelRequired>}
                            {label}
                        </InputLabel>
                    </LabelDiv>
                </MetaSection>

                <InputSection>{fieldGroup}</InputSection>
            </FieldWrapper>
        );
    }

    const labelEl = required ? (
        <label style={{ marginBottom: 6 }}>
            <Space size={8}>
                <LabelRequiredSymbol />
                {label}
            </Space>
        </label>
    ) : (
        <label style={{ marginBottom: 6 }}>{label}</label>
    );

    const labelRow = label ? labelEl : null;

    return (
        <Form.Item style={{ width: '100%', marginBottom: 0 }}>
            {labelRow}
            {fieldGroup}
        </Form.Item>
    );
};

export default FormPhoneNumber;
